import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    FaPhone,
    FaMapMarkerAlt,
    FaEnvelope,
    FaFacebookF,
    FaWhatsapp,
    FaInstagram,
    FaTiktok,
} from 'react-icons/fa';
import logo from '../assets/Navbar/2.png';

const Navbar = () => {
    const { t, i18n } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    const navItems = [
        { to: '/', label: t('home') },
        { to: '/about-us', label: t('aboutUs') },
        { to: '/srilanka', label: t('sriLankanav') },
        { to: '/gallery', label: t('gallerynav') },
        { to: '/tour', label: t('bookATour') },
        { to: '/things', label: t('thingsToDo') },
        { to: '/contact-us', label: t('contactUs') },
    ];

    return (
        <div className="relative">
            {/* Top Bar with Logo, Language & Social Icons */}
            <div
                className={`flex items-center justify-between text-white ${isScrolled ? 'shadow-sm' : ''
                    } bg-black border-b-4 border-gray-500 px-4 sm:px-6 lg:px-8 relative h-40 sm:h-48 md:h-48 transition-all duration-300`}
            >
                {/* Centered Logo */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <img
                        src={logo}
                        alt="Ceylon Tours"
                        className="h-40 sm:h-40 md:h-40 lg:h-48 xl:h-48 w-auto object-contain"
                    />
                </div>

                {/* Desktop: Language Selector + Social Icons */}
                <div className="hidden md:flex flex-col gap-3 absolute top-4 sm:top-6 md:top-8 lg:top-10 right-4 sm:right-6">
                    <select
                        onChange={(e) => changeLanguage(e.target.value)}
                        value={i18n.language}
                        className="lg:ml-32 bg-white text-[#2E4B2C] border border-[#4A7043] rounded-lg px-4 py-2 text-base w-40 shadow-sm hover:bg-gray-100 hover:text-[#4A7043] transition-all focus:outline-none focus:ring-2 focus:ring-[#4A7043]"
                        aria-label="Select language"
                    >
                        <option value="en">English</option>
                        <option value="ru">Russian</option>
                        <option value="ar">Arabic</option>
                    </select>

                    {/* Social Icons */}
                    <div className="flex gap-4 sm:mt-8 md:mt-16">
                        <a href="tel:+94770483364" aria-label="Call us">
                            <FaPhone className="w-6 h-6 text-[#D5A44F] hover:text-[#4A7043] transition-transform hover:scale-110" />
                        </a>
                        <a
                            href="https://maps.app.goo.gl/jJbrFyvjrV2tucNf6"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View location"
                        >
                            <FaMapMarkerAlt className="w-6 h-6 text-[#D5A44F] hover:text-[#4A7043] transition-transform hover:scale-110" />
                        </a>
                        <a href="mailto:aamytourssl@gmail.com" aria-label="Email us">
                            <FaEnvelope className="w-6 h-6 text-[#D5A44F] hover:text-[#4A7043] transition-transform hover:scale-110" />
                        </a>
                        <a
                            href="https://web.facebook.com/profile.php?id=61584113124002"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook"
                        >
                            <FaFacebookF className="w-6 h-6 text-[#D5A44F] hover:text-[#4A7043] transition-transform hover:scale-110" />
                        </a>
                        <a
                            href="https://wa.me/+94770483364"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="WhatsApp"
                        >
                            <FaWhatsapp className="w-6 h-6 text-[#D5A44F] hover:text-[#4A7043] transition-transform hover:scale-110" />
                        </a>
                        <a
                            href="https://www.instagram.com/aamy.tours?igsh=Z21pOWk1dXJ4MzBn&utm_source=qr"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram"
                        >
                            <FaInstagram className="w-6 h-6 text-[#D5A44F] hover:text-[#4A7043] transition-transform hover:scale-110" />
                        </a>
                        <a
                            href="https://www.tiktok.com/@aamy_tours?lang=en"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="TikTok"
                        >
                            <FaTiktok className="w-6 h-6 text-[#D5A44F] hover:text-[#4A7043] transition-transform hover:scale-110" />
                        </a>
                    </div>
                </div>

                {/* Mobile: Language + Hamburger */}
                <div className="md:hidden flex flex-col gap-3 absolute top-4 right-4">
                    <select
                        onChange={(e) => changeLanguage(e.target.value)}
                        value={i18n.language}
                        className="bg-white text-[#2E4B2C] border border-[#4A7043] rounded-lg px-3 py-1.5 text-sm w-32 shadow-sm hover:bg-gray-100 hover:text-[#4A7043] transition-all focus:outline-none focus:ring-2 focus:ring-[#4A7043]"
                        aria-label="Select language"
                    >
                        <option value="en">English</option>
                        <option value="ru">Russian</option>
                        <option value="ar">Arabic</option>
                    </select>

                    <button
                        className="text-[#009CE6] bg-transparent border-none mt-10 ml-20"
                        onClick={toggleMenu}
                        aria-label="Open mobile menu"
                    >
                        <svg
                            className="w-8 h-8"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 6h16M4 12h16m-7 6h7"
                            ></path>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Desktop Navigation Menu */}
            <div
                className="hidden md:flex justify-center items-center px-4 sm:px-6 lg:px-8 py-2 bg-[#D5A44F] sticky top-0 z-50"
                style={{ boxShadow: isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none' }}
            >
                <ul className="flex flex-wrap justify-center gap-4 lg:gap-6 mb-0 font-serif text-xl lg:text-2xl">
                    {navItems.map((item) => (
                        <li key={item.to}>
                            <NavLink
                                to={item.to}
                                className={({ isActive }) =>
                                    `font-copperplate px-4 py-2 text-white hover:text-[#48811D] transition-colors rounded-md ${isActive
                                        ? 'underline bg-[#166880] font-bold'
                                        : ''
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Mobile Sliding Menu */}
            {isMenuOpen && (
                <div
                    className="md:hidden fixed top-0 right-0 h-full w-3/4 max-w-xs bg-[#D5A44F] shadow-2xl border-l border-gray-200 z-50 p-5 rounded-l-lg"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                >
                    <style>
                        {`
              @keyframes slideIn {
                from { transform: translateX(100%); opacity: 0; }
                to { transform: translateX(0); opacity: 1; }
              }
              .animate-slideIn { animation: slideIn 0.3s ease-out; }
            `}
                    </style>

                    {/* Close Button */}
                    <button
                        className="absolute top-4 right-4 bg-yellow-100 text-yellow-800 border border-yellow-800 rounded-full w-12 h-12 flex items-center justify-center text-2xl font-bold hover:bg-yellow-200 hover:scale-110 transition-all"
                        onClick={toggleMenu}
                        aria-label="Close menu"
                    >
                        X
                    </button>

                    {/* Mobile Nav Links */}
                    <ul className="flex flex-col mt-20 gap-3">
                        {navItems.map((item) => (
                            <li key={item.to}>
                                <NavLink
                                    to={item.to}
                                    onClick={toggleMenu}
                                    className={({ isActive }) =>
                                        `font-copperplate block py-3 px-5 text-white text-lg  rounded-md transition-all ${isActive
                                            ? 'bg-[#166880] underline font-bold'
                                            : 'hover:bg-[#166880]'
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    {/* Mobile Social Icons */}
                    <div className="flex gap-5 mt-8 justify-center border-t border-gray-300 pt-6">
                        <a href="tel:+94770483364" aria-label="Call" className="text-black hover:text-[#96BA33] transition-transform hover:scale-110">
                            <FaPhone className="w-6 h-6" />
                        </a>
                        <a href="https://maps.app.goo.gl/jJbrFyvjrV2tucNf6" target="_blank" rel="noopener noreferrer" aria-label="Location" className="text-black hover:text-[#96BA33] transition-transform hover:scale-110">
                            <FaMapMarkerAlt className="w-6 h-6" />
                        </a>
                        <a href="mailto:aamytourssl@gmail.com" aria-label="Email" className="text-black hover:text-[#96BA33] transition-transform hover:scale-110">
                            <FaEnvelope className="w-6 h-6" />
                        </a>
                        <a href="https://web.facebook.com/profile.php?id=61584113124002" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-black hover:text-[#96BA33] transition-transform hover:scale-110">
                            <FaFacebookF className="w-6 h-6" />
                        </a>
                        <a href="https://wa.me/+94770483364" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-black hover:text-[#96BA33] transition-transform hover:scale-110">
                            <FaWhatsapp className="w-6 h-6" />
                        </a>
                        <a href="https://www.instagram.com/aamy.tours?igsh=Z21pOWk1dXJ4MzBn&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-black hover:text-[#96BA33] transition-transform hover:scale-110">
                            <FaInstagram className="w-6 h-6" />
                        </a>
                        <a href="https://www.tiktok.com/@aamy_tours?lang=en" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-black hover:text-[#96BA33] transition-transform hover:scale-110">
                            <FaTiktok className="w-6 h-6" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navbar;